import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CONSULTATION_CREATION } from 'FRONTEND/actions/constants';

const REDUCER_NAME = 'form';

const INSTANCE_ID_SEPARATOR = '__';

export type RepeatedInstance = {
  uuid: string; // Unique identifier for the repeated section instance, provided by the FL RepeatableFieldWrapper.
  fieldId: string; // ID of the field within the repeated section.
  examType: string; // Exam type the repeated section belongs to.
  sectionId: string; // Field ID of the repeatable section.
  value: string; // Value of the repeated field instance.
}

interface RepeatedInstanceEntity extends RepeatedInstance {
  id: string;
}

// Adapter to manage repeated instances of RepeatableFields.
const repeatedInstanceAdapter = createEntityAdapter<RepeatedInstanceEntity>();

// Because of normalization, the id is a composite key built from uuid, fieldId, examType, and sectionId.
// Theoriticaly, uuid and fieldId would be enough,
// but adding examType and sectionId ensures uniqueness across different sections and exams.
const buildRepeatedInstanceId = ({
  uuid,
  fieldId,
  examType,
  sectionId,
}: Omit<RepeatedInstance, 'value'>) => {
  return `${uuid}${INSTANCE_ID_SEPARATOR}${fieldId}${INSTANCE_ID_SEPARATOR}${examType}${INSTANCE_ID_SEPARATOR}${sectionId}`;
};

// Currently only manages repeated instances of RepeatableFields.
// In the future, other form-related state could be added here (once we use React for the rest of the fields).
const formSlice = createSlice({
  name: REDUCER_NAME,
  initialState: repeatedInstanceAdapter.getInitialState(),
  reducers: {
    repeatableInstanceChange: (state, action: PayloadAction<RepeatedInstance>) => {
      const { uuid, fieldId, examType, sectionId, value } = action.payload;
      const id = buildRepeatedInstanceId({ uuid, fieldId, examType, sectionId });
      const existingInstance = state.entities[id];
      if (existingInstance) {
        repeatedInstanceAdapter.updateOne(state, {
          id,
          changes: { value },
        });
      } else {
        repeatedInstanceAdapter.addOne(state, {
          id,
          uuid,
          fieldId,
          examType,
          sectionId,
          value,
        });
      }
    },
    repeatableInstanceRemove: (state, action: PayloadAction<Omit<
      RepeatedInstance,
      'value' |
      'fieldId'
    >>) => {
      const { uuid, examType, sectionId } = action.payload;
      const idPrefix = `${uuid}${INSTANCE_ID_SEPARATOR}`;
      const idsToRemove = Object.keys(state.entities).filter((id) => {
        return id.startsWith(idPrefix) && id.endsWith(`${INSTANCE_ID_SEPARATOR}${examType}${INSTANCE_ID_SEPARATOR}${sectionId}`);
      });
      repeatedInstanceAdapter.removeMany(state, idsToRemove);
    },
  },
  extraReducers: (builder) => {
    // Reset once the consultation creation is done.
    builder.addCase(CONSULTATION_CREATION, (state) => {
      repeatedInstanceAdapter.removeAll(state);
    });
  },
});

export const {
  selectAll: selectAllRepeatedInstances,
} = repeatedInstanceAdapter.getSelectors((state) => state[REDUCER_NAME]);

export const {
  repeatableInstanceChange,
  repeatableInstanceRemove,
} = formSlice.actions;

export default formSlice.reducer;
